import React, { useState, useEffect } from "react";
import { X, Loader2, Flag } from "lucide-react";

export default function ReceiveItemModal({ isOpen, onClose, item, zones, loading, onSubmit }) {
  const [arrived, setArrived] = useState(true);
  const [receivedQty, setReceivedQty] = useState(0);
  const [isFlagged, setIsFlagged] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [zone, setZone] = useState("");
  const [notes, setNotes] = useState("");
  const [showPartial, setShowPartial] = useState(false);

  useEffect(() => {
    if (item) {
      setArrived(true);
      setReceivedQty(item.quantity || 0);
      setIsFlagged(false);
      setFlagReason("");
      setZone("");
      setNotes("");
      setShowPartial(false);
    }
  }, [item]);

  if (!isOpen || !item) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const itemKey = item.vendorKey || item.id;
    onSubmit({ itemId: item.id, itemKey, arrived, receivedQuantity: receivedQty, isFlagged, flagReason, zone, notes });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: "500px" }}>
        <div className="modal-header">
          <h3>Receive Item</h3>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Item Info */}
          <div className="rcv-item-summary">
            <span className="rcv-item-name">{item.description || item.name || "Unnamed Item"}</span>
            <span className="rcv-item-detail">
              Ordered: <strong className="rcv-item-qty">{item.quantity} units</strong>
              {item.vendor ? ` — Vendor: ${item.vendor}` : ""}
            </span>
          </div>

          {/* Arrived Toggle */}
          <div className="rcv-single-row">
            <span className={`rcv-arrival-label ${arrived ? "arrived" : ""}`}>
              {arrived ? "Arrived" : "Not Arrived"}
            </span>
            <label className="switch">
              <input type="checkbox" checked={arrived} onChange={() => { setArrived(!arrived); if (arrived) setReceivedQty(0); else setReceivedQty(item.quantity || 0); }} />
              <span className="slider"></span>
            </label>
          </div>

          {arrived && (
            <div className="rcv-single-controls">
              {/* Action Buttons */}
              <div className="rcv-action-row">
                <button type="button" className={`btn ${showPartial ? "btn-primary" : "btn-secondary"} rcv-action-btn`}
                  onClick={() => { setShowPartial(!showPartial); if (showPartial) setReceivedQty(item.quantity || 0); }}>
                  {showPartial ? "Full Qty" : "Partial"}
                </button>
                <button type="button" className={`btn ${isFlagged ? "btn-danger" : "btn-secondary"} rcv-action-btn rcv-flag-btn`}
                  onClick={() => { setIsFlagged(!isFlagged); if (isFlagged) setFlagReason(""); }}>
                  <Flag size={12} fill={isFlagged ? "currentColor" : "none"} />
                  {isFlagged ? "Flagged" : "Flag Issue"}
                </button>
                <span className="rcv-received-count">
                  Received: <strong>{receivedQty}</strong> / {item.quantity}
                </span>
              </div>

              {/* Partial Qty */}
              {showPartial && (
                <div className="form-group rcv-inline-field">
                  <label className="form-label rcv-field-label">Arrived Quantity</label>
                  <input type="number" className="form-input rcv-qty-input" min="0" max={item.quantity}
                    value={receivedQty} onChange={(e) => setReceivedQty(e.target.value === "" ? "" : Number(e.target.value))} />
                </div>
              )}

              {/* Flag Reason */}
              {isFlagged && (
                <div className="form-group rcv-inline-field">
                  <label className="form-label rcv-field-label rcv-danger-label">What is wrong with the line item?</label>
                  <input type="text" className="form-input rcv-text-input" placeholder="e.g. 5 boxes crushed, water damage"
                    value={flagReason} onChange={(e) => setFlagReason(e.target.value)} required />
                </div>
              )}

              {/* Zone */}
              <div className="form-group rcv-inline-field">
                <label className="form-label rcv-field-label rcv-zone-label">
                  Route To Zone
                  {!zone && <span className="text-danger rcv-required">*Required</span>}
                </label>
                <select className="form-input rcv-select-input" value={zone} onChange={(e) => setZone(e.target.value)} required>
                  <option value="" disabled>Select Zone...</option>
                  {zones && zones.filter(z => !z.isOutline).map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
                </select>
              </div>

              {/* Notes */}
              <div className="form-group rcv-inline-field">
                <input type="text" className="form-input rcv-notes-input" placeholder="Add comments / note for this item..."
                  value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
          )}

          <div className="logistics-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" style={{ backgroundColor: "var(--success)" }} disabled={loading}>
              {loading ? <Loader2 className="logistics-spinner" size={16} /> : null}
              Receive & Advance to Warehouse
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
